import React, { ChangeEvent } from 'react';
import { cn } from '../../lib/utils';

interface FileUploadProps {
  onFileSelect: (fileContent: string, fileType: string) => void;
}

interface UploadedFile {
  name: string;
  size: string;
  type: string;
}

/**
 * FileUpload Component
 *
 * A drag and drop file upload component that supports:
 * - File selection via click
 * - Drag and drop functionality
 * - File type validation
 * - Upload status indication
 * - File removal
 *
 * Accepted file types: TXT, DOC, DOCX, PDF
 */
const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect }) => {
  // State for drag interaction and file info
  const [isDragging, setIsDragging] = React.useState(false);
  const [uploadedFile, setUploadedFile] = React.useState<UploadedFile | null>(null);

  /**
   * Processes the selected file and updates the parent component
   */
  const handleFile = (file: File) => {
    console.log('Processing file:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        let content = '';
        if (file.type === 'application/pdf') {
          // For PDFs, read as ArrayBuffer and convert to base64
          const arrayBuffer = e.target.result as ArrayBuffer;
          // Use a more efficient method to convert ArrayBuffer to base64
          const uint8Array = new Uint8Array(arrayBuffer);
          let binaryString = '';
          for (let i = 0; i < uint8Array.length; i++) {
            binaryString += String.fromCharCode(uint8Array[i]);
          }
          content = btoa(binaryString);
        } else {
          // For text files, use the result directly
          content = e.target.result as string;
        }
        onFileSelect(content, file.type);
        setUploadedFile({
          name: file.name,
          size: formatFileSize(file.size),
          type: file.type
        });
        console.log('File processed successfully');
      }
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
    };

    // Use readAsArrayBuffer for PDFs, readAsText for others
    if (file.type === 'application/pdf') {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  /**
   * Handles the drag over event
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  /**
   * Handles the drag leave event
   */
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  /**
   * Handles the file drop event
   */
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  /**
   * Handles the file input change event
   */
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  /**
   * Handles file removal
   */
  const handleRemoveFile = () => {
    console.log('Removing file:', uploadedFile?.name);
    setUploadedFile(null);
    onFileSelect('', '');
  };

  /**
   * Formats the file size into a readable string
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "mt-4 border-2 border-dashed rounded-xl p-4 text-center transition-all duration-200 group relative",
        isDragging
          ? "border-blue-500 bg-blue-50"
          : uploadedFile
            ? "border-green-500 bg-green-50"
            : "border-gray-200 hover:border-gray-300"
      )}
    >
      {uploadedFile ? (
        // Successful upload state
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-700">{uploadedFile.name}</p>
              <p className="text-xs text-gray-500">{uploadedFile.size}</p>
            </div>
          </div>
          <button
            onClick={handleRemoveFile}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Remove file"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      ) : (
        // Upload prompt state
        <label className="cursor-pointer block">
          <div className="flex flex-col items-center justify-center">
            <svg
              className={cn(
                "w-8 h-8 mb-2 transition-all duration-200",
                isDragging ? "text-blue-500 scale-110" : "text-gray-400 group-hover:text-blue-500"
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className={cn(
              "text-sm mb-1 transition-colors duration-200",
              isDragging ? "text-blue-600 font-medium" : "text-gray-600 group-hover:text-gray-900"
            )}>
              {isDragging ? 'Drop your file here' : 'Drag and drop or click to browse'}
            </p>
            <p className="text-xs text-gray-500">TXT, DOC, DOCX, PDF</p>
          </div>
          <input
            type="file"
            onChange={handleFileChange}
            className="hidden"
            accept=".txt,.doc,.docx,.pdf"
          />
        </label>
      )}
    </div>
  );
};

export default FileUpload;
